import React, { useEffect, useState } from "react";
import {
  Box,
  PageLayout,
  Text,
  ActionList,
  ActionMenu,
  Button,
  TextInput,
  FormControl,
  ButtonGroup,
} from "@primer/react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  SyncIcon,
  KebabHorizontalIcon,
  DownloadIcon,
  ListOrderedIcon,
  MailIcon,
  DeviceMobileIcon,
} from "@primer/octicons-react";
import { Dialog } from "@primer/react/drafts";

import { useSelector } from "react-redux";
import { useCommonContext } from "../../../contexts/common";
import { NotifyHelper } from "../../../helpers/toast";
import { thiSinhApi } from "../../../api/thiSinhApi";
import MyButton from "../../../components/ui/button/Button";
import DataTable from "../../../components/ui/data-table";
import { ComboboxTruong } from "../../../components/combobox-truong";
import ComboboxKyThi from "../../../components/combobox-kythi";
import AddThiSinhModal from "./add-thi-sinh/AddThiSinhModal";
import DanhSoBaoDanhModal from "./danh-so-bao-danh/DanhSoBaoDanhModal";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import moment from "moment";

interface ThiSinhData {
  id: number;
  trang_thai: string;
  so_bao_danh: string;
  ngaythi_dauvao_dukien: string;
  ma_hs: string;
  ho_ten: string;
  ngay_sinh: string;
  gioi_tinh: string;
  ten_khoi: string;
  ten_he: string;
}

interface FilterData {
  dm_truong_id: number;
  nam_hoc: string;
  ex_kythi_id: number;
}

const ThiSinh: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [thiSinhs, setThiSinhs] = useState<ThiSinhData[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBaoDanhModal, setShowBaoDanhModal] = useState(false);
  const [kyThiKetThuc, setKyThiKetThuc] = useState(false);
  const [filter, setFilter] = useState<FilterData>({
    dm_truong_id: 0,
    nam_hoc: "",
    ex_kythi_id: 0,
  });

  const { translate } = useCommonContext();
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  const loadThiSinhs = async () => {
    setIsLoading(true);
    try {
      const res = await thiSinhApi.select(filter);
      if (res.is_success) {
        setThiSinhs(res.data);
      } else {
        setThiSinhs([]);
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi khi tải dữ liệu");
    }
    setIsLoading(false);
  };

  const handleDelete = async () => {
    try {
      const res = await thiSinhApi.deleteMultiple({
        ex_thisinh_ids: selectedIds,
      });
      if (res.is_success) {
        NotifyHelper.Success("Xóa thí sinh thành công");
        setShowDeleteConfirm(false);
        await loadThiSinhs();
      } else {
        NotifyHelper.Error(res.message || "Có lỗi");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi");
    }
  };

  useEffect(() => {
    if (nam_hoc) {
      setFilter((prev) => ({ ...prev, nam_hoc }));
    }
  }, [nam_hoc]);

  useEffect(() => {
    setFilter({
      dm_truong_id: 0,
      nam_hoc,
      ex_kythi_id: 0,
    });
  }, [dm_coso_id]);

  useEffect(() => {
    loadThiSinhs();
  }, [filter]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
        <FormControl sx={{ width: "300px" }}>
          {/* <FormControl.Label>Trường</FormControl.Label> */}
          <ComboboxTruong
            value={filter.dm_truong_id}
            onValueChanged={(value) =>
              setFilter((prev) => ({ ...prev, dm_truong_id: value || 0 }))
            }
          />
        </FormControl>

        <FormControl sx={{ width: "400px" }}>
          {/* <FormControl.Label>Kỳ thi</FormControl.Label> */}
          <ComboboxKyThi
            value={filter.ex_kythi_id}
            dm_truong_id={filter.dm_truong_id}
            onValueChanged={(value, da_ket_thuc) => {
              setFilter((prev) => ({ ...prev, ex_kythi_id: value || 0 }));
              setKyThiKetThuc(da_ket_thuc.da_ket_thuc);
            }}
          />
        </FormControl>
      </Box>
      <DataTable
        title={translate("Thí sinh")}
        subTitle={`${translate("Base.Label.TotalCount")}: ${thiSinhs.length}`}
        height={`${window.innerHeight - 300}px`}
        data={thiSinhs}
        isLoading={isLoading}
        searchEnable
        paging={{ enable: true, pageSize: 50 }}
        selection={{
          mode: "multiple",
          selectedRowKeys: selectedIds,
          onSelectionChanged: setSelectedIds,
        }}
        actionComponent={
          <Box sx={{ display: "flex", gap: 2 }}>
            {kyThiKetThuc && (
              <Text
                as="span"
                sx={{
                  ml: 2,
                  p: 1,
                  bg: "danger.emphasis",
                  color: "white",
                  borderRadius: 2,
                }}
              >
                Kỳ thi đã kết thúc
              </Text>
            )}
            <MyButton
              text="Thêm thí sinh"
              leadingVisual={PlusIcon}
              variant="primary"
              disabled={
                filter.dm_truong_id <= 0 ||
                filter.ex_kythi_id <= 0 ||
                kyThiKetThuc
              }
              onClick={() => setShowAddModal(true)}
            />
            <MyButton
              text="Đánh số báo danh"
              leadingVisual={ListOrderedIcon}
              disabled={thiSinhs.length <= 0 || kyThiKetThuc}
              onClick={() => setShowBaoDanhModal(true)}
            />
            <MyButton
              text="Gửi SMS"
              leadingVisual={DeviceMobileIcon}
              disabled={true}
            />
            <MyButton
              text="Gửi Email"
              leadingVisual={MailIcon}
              disabled={true}
            />
            <MyButton
              text={`Xóa thí sinh`}
              leadingVisual={TrashIcon}
              variant="danger"
              disabled={selectedIds.length <= 0 || kyThiKetThuc}
              onClick={() => setShowDeleteConfirm(true)}
            />
            <MyButton
              text="Tải lại dữ liệu"
              leadingVisual={SyncIcon}
              onClick={loadThiSinhs}
            />
          </Box>
        }
        columns={[
          // {
          //   caption: "TT",
          //   width: "50px",
          //   align: "center",
          //   cellRender: (data: any) => {
          //     const pageSize = 50; // Khớp với pageSize trong DataTable
          //     const pageIndex = data.component?.pageIndex ?? 0;
          //     const rowIndex = data.index + (pageSize * pageIndex) + 1;
          //     return <Text>{rowIndex}</Text>;
          //   },
          // },

          {
            dataField: "trang_thai",
            caption: "Trạng thái",
          },
          {
            dataField: "so_bao_danh",
            caption: "Số báo danh",
          },
          {
            dataField: "ngaythi_dauvao_dukien",
            caption: "Ngày thi dự kiến",
          },
          {
            dataField: "ma_hs",
            caption: "Mã học sinh",
          },
          {
            dataField: "ho_ten",
            caption: "Họ tên",
          },
          {
            dataField: "ngay_sinh",
            caption: "Ngày sinh",
            cellRender: (data) => (
              <Text>
                {data.ngay_sinh ? moment(data.ngay_sinh).format('DD/MM/YYYY') : ''}
              </Text>
            )
          },
          {
            dataField: "gioi_tinh",
            caption: "Giới tính",
          },
          {
            dataField: "ten_khoi",
            caption: "Khối",
          },
          {
            dataField: "ten_he",
            caption: "Hệ",
          },
        ]}
      />

      {showAddModal && (
        <AddThiSinhModal
          onCancel={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadThiSinhs();
          }}
          dm_truong_id={filter.dm_truong_id}
          ex_kythi_id={filter.ex_kythi_id}
          nam_hoc={filter.nam_hoc}
        />
      )}

      {showBaoDanhModal && (
        <DanhSoBaoDanhModal
          onCancel={() => setShowBaoDanhModal(false)}
          onSuccess={() => {
            setShowBaoDanhModal(false);
            loadThiSinhs();
          }}
          dm_truong_id={filter.dm_truong_id}
          ex_kythi_id={filter.ex_kythi_id}
          nam_hoc={filter.nam_hoc}
        />
      )}

      {showDeleteConfirm && (
        <Dialog
          title={translate("Lưu ý")}
          onClose={() => setShowDeleteConfirm(false)}
        >
          <Dialog.Body>
            {translate(
              `Bạn có chắc chắn muốn xóa ${selectedIds.length} thí sinh đã chọn ?`
            )}
          </Dialog.Body>
          <Dialog.Footer>
            <ButtonGroup>
              <Button onClick={() => setShowDeleteConfirm(false)}>
                {translate("Base.Label.Cancel")}
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                {translate("Base.Label.Delete")}
              </Button>
            </ButtonGroup>
          </Dialog.Footer>
        </Dialog>
      )}
    </Box>
  );
};

export default ThiSinh;
